import { useMemo, useState } from "react"
import { Show, useObservable } from "@legendapp/state/react"
import { ActivityIndicator, Pressable, View } from "react-native"
import { Link } from "expo-router"
import { SafeAreaView } from "react-native-safe-area-context"

import { ar } from "@src/i18n/ar"
import { cn, sharedStyles, trpc, formatDateForDisplay } from "@src/utils"
import { List, Header, Text, Button, MCIcon } from "@src/components"
import { NotificationComposer } from "@src/components/notification-composer"
import { previewAudienceInput, type z } from "@white-labels/input"

type PageAudience = Extract<z.infer<typeof previewAudienceInput>, { audience: "all" | "registered" | "unregistered" | "group" }>

const audiences = [
  { value: "registered", label: ar.notificationAudienceRegistered },
  { value: "unregistered", label: ar.notificationAudienceUnregistered },
  { value: "all", label: ar.notificationAudienceAll },
  { value: "group", label: ar.notificationAudienceGroup },
] as const satisfies readonly { value: PageAudience["audience"]; label: string }[]

// History rows persist the DB enum; `group` rows carry a name snapshot.
function historyAudienceLabel(audience: string, label: string | null): string {
  switch (audience) {
    case "all":
      return ar.notificationAudienceAll
    case "registered":
      return ar.notificationAudienceRegistered
    case "unregistered":
      return ar.notificationAudienceUnregistered
    case "group":
      return label ?? ar.notificationAudienceGroup
    default:
      return ar.notificationAudienceCustomer
  }
}

export default function Notifications() {
  const composerOpen$ = useObservable(false)
  const [audienceKind, setAudienceKind] = useState<PageAudience["audience"]>("registered")
  const [groupId, setGroupId] = useState<number | null>(null)

  // The complete audience input; undefined while `group` is selected but no
  // group has been picked yet (preview and send stay disabled until it exists).
  const audience: PageAudience | undefined =
    audienceKind === "group" ? (groupId !== null ? { audience: "group", groupId } : undefined) : { audience: audienceKind }

  const groups = trpc.notifications.groups.list.useQuery(undefined, { enabled: audienceKind === "group" })
  const groupList = useMemo(() => groups.data ?? [], [groups.data])
  const preview = trpc.notifications.previewAudience.useQuery(audience ?? { audience: "registered" }, {
    staleTime: 30_000,
    enabled: audience !== undefined,
  })
  const history = trpc.notifications.history.useInfiniteQuery({}, { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined })
  const historyItems = useMemo(() => history.data?.pages.flatMap((p) => p.items) ?? [], [history.data])

  const eligibleCount = preview.data?.eligibleCount ?? 0
  const optedOutCount = preview.data?.optedOutCount ?? 0
  const exceedsLimit = preview.data?.exceedsSendLimit
  // Mirrors the composer's guard: only open for a valid, non-empty audience.
  const canSend = audience !== undefined && preview.isSuccess && eligibleCount > 0 && !exceedsLimit

  const refresh = async () => {
    await Promise.all([preview.refetch(), history.refetch()])
  }

  return (
    <>
      <Header
        title={ar.notifications}
        right={
          <Link href="/dashboard/notifications/groups" asChild>
            <Pressable className="p-2 h-full justify-center active:opacity-40">
              <MCIcon name="account-group" size={24} color="#000" />
            </Pressable>
          </Link>
        }
      />

      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={historyItems}
        keyExtractor={(item) => item.id.toString()}
        onRefresh={refresh}
        onEndReached={() => {
          if (history.hasNextPage && !history.isFetchingNextPage) history.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        ListHeaderComponent={
          <View className="mb-5">
            <Text className="text-sm font-bold text-gray-900 mb-2">{ar.notificationAudience}</Text>
            <View className="flex-row gap-2 mb-3">
              {audiences.map((a) => {
                const active = audienceKind === a.value
                return (
                  <Pressable
                    key={a.value}
                    onPress={() => setAudienceKind(a.value)}
                    className={cn("flex-1 py-2 rounded border active:opacity-80", active ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300")}
                  >
                    <Text className={cn("text-center text-sm font-bold", active ? "text-white" : "text-gray-700")}>{a.label}</Text>
                  </Pressable>
                )
              })}
            </View>

            {audienceKind === "group" && (
              <View className="mb-3">
                {groups.isLoading ? (
                  <ActivityIndicator />
                ) : groups.isError ? (
                  <Text className="text-red-600 text-xs">{ar.errorLoading}</Text>
                ) : groupList.length === 0 ? (
                  <Text className="text-gray-500 text-xs">{ar.notificationNoGroups}</Text>
                ) : (
                  <View className="flex-row flex-wrap gap-2">
                    {groupList.map((g) => {
                      const active = groupId === g.id
                      return (
                        <Pressable
                          key={g.id}
                          onPress={() => setGroupId(g.id)}
                          className={cn("px-3 py-1.5 rounded-full border active:opacity-80", active ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300")}
                        >
                          <Text className={cn("text-sm font-bold", active ? "text-white" : "text-gray-700")}>
                            {g.name} ({g.memberCount})
                          </Text>
                        </Pressable>
                      )
                    })}
                  </View>
                )}
              </View>
            )}

            <View className="bg-white rounded border border-gray-100 shadow-sm p-3 mb-3">
              {audience === undefined ? (
                <Text className="text-gray-500 text-xs">{ar.notificationSelectGroup}</Text>
              ) : preview.isLoading ? (
                <ActivityIndicator />
              ) : preview.isError ? (
                <Text className="text-red-600 text-xs">{ar.errorLoading}</Text>
              ) : (
                <>
                  {/* Authoritative sendable-recipient count from the shared resolver. */}
                  <Text className="text-gray-500 text-xs">
                    {ar.notificationEligible}: {eligibleCount}
                  </Text>
                  {optedOutCount > 0 && (
                    <Text className="text-gray-400 text-xs mt-1">
                      {ar.notificationExcluded}: {optedOutCount}
                    </Text>
                  )}
                  {exceedsLimit && <Text className="text-red-600 text-xs mt-1">{ar.notificationSendLimit}</Text>}
                  {eligibleCount === 0 && <Text className="text-gray-500 text-xs mt-1">{ar.notificationNoRecipients}</Text>}
                </>
              )}
            </View>

            <Button onPress={() => composerOpen$.set(true)} disabled={!canSend}>
              {ar.sendNotification}
            </Button>

            <Text className="text-sm font-bold text-gray-900 mt-5 mb-2">{ar.notificationSendHistory}</Text>
          </View>
        }
        ListEmptyComponent={
          history.isLoading ? null : (
            <View className="mt-2">
              <Text className="text-center text-gray-500">{ar.notificationHistoryEmpty}</Text>
            </View>
          )
        }
        renderItem={({ item }) => (
          <View className="bg-white rounded border-gray-100 p-3 mb-1 shadow-sm">
            <View className="flex-row items-center justify-between mb-1 gap-2">
              <View className="bg-blue-50 rounded px-2 py-0.5 shrink-0">
                <Text className="text-blue-700 text-xs font-bold">{historyAudienceLabel(item.audience, item.audienceLabel)}</Text>
              </View>
              <Text className="text-xs text-gray-400">{formatDateForDisplay(item.createdAt, "D MMM • hh:mm a")}</Text>
            </View>
            <Text className="font-bold text-gray-800 mb-0.5" numberOfLines={1}>
              {item.title}
            </Text>
            <Text className="text-sm text-gray-500 mb-1.5 leading-5" numberOfLines={2}>
              {item.body}
            </Text>
            <View className="flex-row flex-wrap gap-3">
              <Text className="text-xs text-gray-400">
                {ar.notificationEligible}: {item.eligibleCount}
              </Text>
              <Text className="text-xs text-green-600">
                {ar.notificationAccepted}: {item.acceptedCount}
              </Text>
              <Text className="text-xs text-red-600">
                {ar.notificationRejected}: {item.rejectedCount}
              </Text>
              <Text className="text-xs text-gray-400">
                {ar.notificationSkipped}: {item.skippedCount}
              </Text>
            </View>
          </View>
        )}
        ListFooterComponent={
          <>
            {history.isFetchingNextPage && (
              <View className="py-4 items-center">
                <ActivityIndicator size="small" color="#3B82F6" />
              </View>
            )}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
      />

      <Show if={composerOpen$}>{() => (audience ? <NotificationComposer audience={audience} onClose={() => composerOpen$.set(false)} /> : null)}</Show>
    </>
  )
}
